import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { getKids, setDefaultKid, getKidFullDetails } from '../contexts/db.Kids';
import KidProgressModal from '../components/KidProgressModal';
import '../styles/pages/KidsPage.css';

function KidsPage({ language }) {
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKid, setSelectedKid] = useState(null);
  const [kidDetails, setKidDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);
  
  const navigate = useNavigate();
  const t = translations[language];

  // Get userId from localStorage
  const userId = (() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      return userData?.id;
    } catch { return null; }
  })();

  useEffect(() => {
    const fetchKids = async () => {
      if (!userId) {
        setError(t.missingUserInfo || 'User information is missing.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const kidsData = await getKids(userId);
        setKids(Array.isArray(kidsData) ? kidsData : kidsData?.kids || []);
      } catch (err) {
        console.error('Error fetching kids:', err);
        setError(t.fetchKidsFailed || 'Failed to load kids.');
      } finally {
        setLoading(false);
      }
    };

    fetchKids();
  }, [userId]);

  const handleSetDefault = async (kidId) => {
    try {
      setSettingDefault(kidId);
      await setDefaultKid(kidId, userId);
      
      // Update local state to reflect the change
      setKids(prevKids => prevKids.map(kid => ({
        ...kid,
        is_default: kid.id === kidId ? 1 : 0
      })));
    } catch (err) {
      console.error('Error setting default kid:', err);
      setError(t.setDefaultFailed || 'Failed to set default kid.');
    } finally {
      setSettingDefault(null);
    }
  };

  const handleKidClick = async (kid) => {
    setSelectedKid(kid);
    setShowModal(true);
    
    try {
      const details = await getKidFullDetails(kid.id);
      setKidDetails(details);
    } catch (err) {
      console.error('Error fetching kid details:', err);
      setKidDetails({ kid_id: kid.id, coin_balance: 0, progress: [] });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedKid(null);
    setKidDetails(null);
  };

  if (loading) {
    return (
      <div className="kids-page-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>{t.loadingKids || 'Loading kids...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kids-page-container">
      <div className="kids-header">
        <h1>{t.myKids || 'My Kids'}</h1>
        <p>{t.kidsDescription || 'Manage your children\'s profiles and view their progress'}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {kids.length === 0 ? (
        <div className="no-kids">
          <div className="no-kids-icon">👶</div>
          <p>{t.noKids || 'No kids added yet'}</p>
        </div>
      ) : (
        <div className="kids-grid">
          {kids.map((kid) => (
            <div 
              key={kid.id} 
              className={`kid-card ${kid.is_default === 1 ? 'default' : ''}`}
              onClick={() => handleKidClick(kid)}
            >
              {kid.is_default === 1 && (
                <div className="default-badge">
                  {t.default || 'Default'}
                </div>
              )}
              
              <div className="kid-avatar">
                {kid.avatar ? (
                  <img src={kid.avatar} alt={kid.first_name} />
                ) : (
                  <div className="avatar-placeholder">
                    {kid.first_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
              </div>
              
              <div className="kid-info">
                <h3>{kid.first_name} {kid.last_name}</h3>
                {kid.kid_age && <p className="kid-age">{kid.kid_age} {t.yearsOld || 'years old'}</p>}
              </div>
              
              {kid.is_default !== 1 && (
                <button 
                  className="set-default-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSetDefault(kid.id);
                  }}
                  disabled={settingDefault === kid.id}
                >
                  {settingDefault === kid.id 
                    ? (t.setting || 'Setting...') 
                    : (t.setAsDefault || 'Set as Default')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && selectedKid && (
        <KidProgressModal
          kid={selectedKid}
          details={kidDetails}
          language={language}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default KidsPage;
